import React, { useEffect, useRef, useState } from 'react';
import { ConversationMessage } from '../../types/capstone';
import { stripPlanBlocks } from '../../services/capstoneApi';

export interface ChatToolEvent {
  id: string;
  tool: string;
  status: 'running' | 'success' | 'failed';
  summary?: string;
  // Present for run_command events — lets the card offer a "↻ Re-run".
  command?: string;
  // Which conversation turn (iteration) this card belongs to, so it renders
  // inline with that turn instead of piling up at the bottom of the chat.
  iteration: number;
}

interface Props {
  // Persisted conversation loaded from the server (oldest first)
  initialMessages: ConversationMessage[];
  // Streaming assistant text for the in-flight iteration; appears as the last
  // message bubble until the iteration completes.
  streamingAssistant: string | null;
  // Post-success learner recap ("What you built / Try it / Your files") —
  // streams AFTER the latest turn's tool cards as its own bubble, and stays
  // there until the next turn starts (the page folds it into messages then).
  streamingRecap?: string | null;
  // Tool calls executed by the orchestrator during the current session.
  // Rendered as inline activity cards within the chat flow.
  toolEvents: ChatToolEvent[];
  // True while an iterate request is in flight (input disabled, button shows spinner)
  busy: boolean;
  // Called when the user submits a message
  onSend: (text: string) => void;
  // Re-run a previous run_command directly against the container (no LLM).
  // When omitted, run_command cards don't show a re-run button.
  onRerun?: (command: string) => void;
  // Fired when the user focuses the input — the page uses it as an intent
  // signal to wake a hibernated project immediately (skip the grace period).
  onInputFocus?: () => void;
}

const Bubble: React.FC<{ role: 'user' | 'assistant'; content: string; streaming?: boolean }> = ({
  role, content, streaming
}) => {
  const isUser = role === 'user';
  const displayContent = isUser ? content : stripPlanBlocks(content);
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
          isUser
            ? 'bg-purple-600 text-white rounded-br-sm'
            : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-bl-sm'
        }`}
      >
        {displayContent}
        {streaming && (
          <span className="inline-block w-2 h-4 ml-1 bg-current opacity-60 animate-pulse" />
        )}
      </div>
    </div>
  );
};

const ToolEventCard: React.FC<{
  evt: ChatToolEvent;
  busy: boolean;
  onRerun?: (command: string) => void;
}> = ({ evt, busy, onRerun }) => {
  const tone =
    evt.status === 'running' ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' :
    evt.status === 'success' ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' :
    'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
  const label =
    evt.status === 'running' ? 'running…' :
    evt.status === 'success' ? '✓' :
    '✕';
  const canRerun = evt.tool === 'run_command' && !!evt.command && !!onRerun;
  return (
    <div className="flex justify-start mb-2">
      <div className={`max-w-[85%] rounded-lg border px-3 py-1.5 text-xs ${tone}`}>
        <div className="flex items-center gap-2">
          <span className="font-mono font-semibold text-gray-800 dark:text-gray-100">
            {evt.tool}
          </span>
          <span className="text-gray-500 dark:text-gray-400">{label}</span>
          {canRerun && (
            <button
              type="button"
              onClick={() => onRerun!(evt.command!)}
              disabled={busy}
              title={`Re-run: ${evt.command}`}
              className="ml-auto shrink-0 px-1.5 py-0.5 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-white/70 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ↻ Re-run
            </button>
          )}
        </div>
        {evt.summary && (
          <div className="mt-0.5 text-gray-600 dark:text-gray-300 font-mono truncate">
            {evt.summary}
          </div>
        )}
      </div>
    </div>
  );
};

const ChatPane: React.FC<Props> = ({ initialMessages, streamingAssistant, streamingRecap, toolEvents, busy, onSend, onRerun, onInputFocus }) => {
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom whenever messages or streaming content changes
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [initialMessages, streamingAssistant, streamingRecap, toolEvents]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    onSend(text);
    setInput('');
  };

  // Render the chat as a per-turn timeline: each iteration shows its
  // user/assistant messages, then THAT turn's tool cards — so activity cards
  // sit with their turn instead of piling up at the bottom.
  const iterations = Array.from(
    new Set<number>([
      ...initialMessages.map((m) => m.iteration),
      ...toolEvents.map((e) => e.iteration),
    ]),
  ).sort((a, b) => a - b);

  // The in-flight turn (assistant still streaming) is the highest iteration.
  const streamingIteration =
    streamingAssistant !== null && initialMessages.length > 0
      ? Math.max(...initialMessages.map((m) => m.iteration))
      : null;

  const isEmpty =
    initialMessages.length === 0 && streamingAssistant === null && toolEvents.length === 0;

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900">
      {/* Scrolling message list — per-turn: messages then that turn's tool cards */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3">
        {isEmpty && (
          <div className="text-center text-sm text-gray-500 dark:text-gray-400 mt-12">
            Type a message below to start the conversation.
          </div>
        )}
        {iterations.map((it) => (
          <React.Fragment key={`turn-${it}`}>
            {initialMessages
              .filter((m) => m.iteration === it)
              .map((m, idx) => (
                <Bubble key={`m-${it}-${idx}`} role={m.role} content={m.content} />
              ))}
            {streamingIteration === it && streamingAssistant !== null && (
              <Bubble role="assistant" content={streamingAssistant} streaming />
            )}
            {toolEvents
              .filter((e) => e.iteration === it)
              .map((e) => (
                <ToolEventCard key={e.id} evt={e} busy={busy} onRerun={onRerun} />
              ))}
            {/* Post-success recap: after the LATEST turn's cards — anchored to
                the last iteration (not streamingIteration, which nulls when the
                main stream ends before/while the recap arrives). */}
            {it === iterations[iterations.length - 1] && streamingRecap ? (
              <Bubble role="assistant" content={streamingRecap} streaming />
            ) : null}
          </React.Fragment>
        ))}
        {/* Safety net: streaming with no message yet (shouldn't normally happen) */}
        {streamingAssistant !== null && streamingIteration === null && (
          <Bubble role="assistant" content={streamingAssistant} streaming />
        )}
      </div>

      {/* Composer */}
      <form
        onSubmit={handleSubmit}
        className="border-t border-gray-200 dark:border-gray-800 p-3 bg-gray-50 dark:bg-gray-900"
      >
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onFocus={onInputFocus}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e as unknown as React.FormEvent);
              }
            }}
            placeholder={busy ? 'Capstone is thinking…' : 'Tell Capstone what to build or change…'}
            rows={2}
            disabled={busy}
            className="flex-1 resize-none rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={busy || !input.trim()}
            className="rounded-lg bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-4 py-2 text-sm font-medium"
          >
            {busy ? '…' : 'Send'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatPane;
